import * as React from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {useHistory} from 'src/rerouter/index';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';

import {EXTENSION_ID} from 'src/chat-extension/constants';
import logger from 'src/utils/logger';

import PortContext from 'src/components/messaging/port-context';

import {selectFaroEnabledExtensionAgentIds} from 'src/selectors/agency';
import {getCurrentAgent} from 'src/selectors/accounts';
import {setMeetingsScheduleEventData} from 'src/action-creators/messages';
import {setMsgExtLocationData} from 'src/action-creators/draft-messages';
import {routeExternalContact} from 'src/action-creators/contacts';
import {observability} from 'src/utils/observability';

import {
  CLICKED_NUMBER,
  PORT_OPEN,
  ANALYTICS_TRACK,
  MEETING_SCHEDULE_EVENT,
  CLICKED_JOB_ID_CELL,
  CONVERSATION_LINK_CLICKED,
} from 'src/chat-extension/messages';
import {isAgentSelectedForAmplitudeTracking} from 'src/selectors/messages-v2';

import {AnalyticsService} from 'src/analytics/index';
import {getMessagingTrackingMetrics} from 'src/components/messaging/messaging-amplitude-helper';


const initialExtensionTimeout = 200;

const ExtensionConnection = ({children}: {children: React.ReactNode}) => {
  const {isExtension} = useEnvironmentContext();
  const {changeLocation} = useWebAndExtRouterControl();
  const router = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const faroExtensionAgentIds = useSelector(selectFaroEnabledExtensionAgentIds);
  const agentId = useSelector(getCurrentAgent)?.id;
  const trackObservabilityEvent =
    agentId && faroExtensionAgentIds.includes(agentId);

  const [extensionPortValue, setExtensionPortValue] = React.useState<any>(null);
  const [extensionConnectionTimeout, setExtensionConnectionTimeout] =
    React.useState(initialExtensionTimeout);
  const trackAmplitudeEvent = useSelector(isAgentSelectedForAmplitudeTracking);

  const handleMessage = (message: any) => {
    // For all the observability events, we just need to log the events in this file,
    // when the user id is present in faroExtensionAgentIds, else these events are not needed.
    // [note:] but since the initialization happens on the web side,
    // we need to have the user id in the faroWebAgentIds state.
    if (trackObservabilityEvent) {
      observability.log('[EXTENSION_CONNECTION] received message', {
        message: JSON.stringify(message),
      });
    }
    if (message.type === PORT_OPEN) {
      setExtensionConnectionTimeout(initialExtensionTimeout);
    } else if (message.type === MEETING_SCHEDULE_EVENT) {
      if (trackObservabilityEvent) {
        observability.log(
          '[MEETING_SCHEDULE_EVENT] received in extension connection',
          {
            message: JSON.stringify(message),
          },
        );
      }
      const payload = message.payload;
      setTimeout(() => {
        dispatch(setMeetingsScheduleEventData(payload));
        changeLocation(router, {
          ...router.location,
          pathname: '/meetings/schedule-now',
        });
      }, 0);
    } else if (message.type === CLICKED_NUMBER) {
      setTimeout(() => {
        if (trackObservabilityEvent) {
          observability.log(
            '[CLICKED_NUMBER] received in extension connection',
            {
              message: JSON.stringify(message),
            },
          );
        }
        dispatch(routeExternalContact(router, message));
        if (trackAmplitudeEvent) {
          const {mode, domain, path, fullPath} = message.payload;
          const trackingDetails = getMessagingTrackingMetrics();
          AnalyticsService.track('Extension Number Clicked', {
            ...trackingDetails,
            mode,
            domain,
            path,
            fullPath,
          });
        }
      }, 0);
    } else if (message.type === ANALYTICS_TRACK) {
      const {event, data} = message.payload;
      setTimeout(() => {
        if (event === 'EXTENSION ATS URL') {
          if (trackAmplitudeEvent) {
            // we need to set these details to redux state to fetch
            // as this is need for multiple events
            dispatch(setMsgExtLocationData(data));
          }
        } else {
          if (trackAmplitudeEvent) {
            const trackingDetails = getMessagingTrackingMetrics();
            AnalyticsService.track(event, {
              trackingDetails,
              ...data,
            });
          }
        }
      }, 0);
    } else if (message.type === CLICKED_JOB_ID_CELL) {
      const {id} = message.payload;
      if (id) {
        const jobId = String(id);
        setTimeout(() => {
          const currentJobId = router.location.query?.jobId;
          if (currentJobId === jobId) {
            // Same job clicked again — invalidate so React Query refetches.
            queryClient.invalidateQueries({
              queryKey: ['recruiter-hub-matches'],
            });
            queryClient.invalidateQueries({queryKey: ['recruiter-hub-counts']});
          }
          // In the chrome extension we always land on Pipeline — Dashboard
          // and Analytics aren't reachable there. Web stays on the bare URL
          // so the existing RecruiterHubRedirect logic can route admins to
          // Dashboard and non-admins to Pipeline.
          const targetPathname = isExtension
            ? '/recruiter-hub-v2/pipeline'
            : '/recruiter-hub-v2';
          changeLocation(router, {
            ...router.location,
            pathname: targetPathname,
            query: {...(router.location.query ?? {}), jobId},
          });
        }, 0);
      }
    } else if (message.type === CONVERSATION_LINK_CLICKED) {
      const conversationLink =
        typeof message.payload?.conversationLink === 'string'
          ? message.payload.conversationLink.trim()
          : '';

      setTimeout(() => {
        changeLocation(router, {
          pathname: '/voiceflow/conversations',
          query: {cl: conversationLink},
        });
      }, 0);
    }
  };

  const handleDisconnect = (port: any) => {
    const error =
      port.error ||
      (window.chrome &&
        window.chrome.runtime &&
        window.chrome.runtime.lastError);
    logger.log('Port disconnecting');
    if (error) {
      logger.error('Disconnected from extension.', error);
    }

    port.onMessage.removeListener(handleMessage);

    logger.log(
      `Attempting to reconnect to extension in ${extensionConnectionTimeout}ms...`,
    );
    setTimeout(() => connectToExtension(), extensionConnectionTimeout);
    setExtensionConnectionTimeout((prev) => prev * 2);
    setExtensionPortValue(null);
  };

  const connectToExtension = () => {
    // @ts-expect-error - TS2339 - Property 'chrome' does not exist on type 'Window & typeof globalThis'.
    const extensionPort = window.chrome.runtime.connect(EXTENSION_ID, {
      name: 'chat-iframe',
    });
    setExtensionPortValue(extensionPort);
    if (extensionPort) {
      extensionPort.onMessage.addListener(handleMessage);
      extensionPort.onDisconnect.addListener(handleDisconnect);
    }
  };

  React.useEffect(() => {
    if (window.chrome && window.chrome.runtime && isExtension) {
      connectToExtension();
    }
  }, []);

  return (
    <PortContext.Provider value={extensionPortValue}>
      {children}
    </PortContext.Provider>
  );
};

export default ExtensionConnection;
